import type { Comment, CommentAuthor, CommentReply } from '../model/types';

import type { UserAvatarUrlLookup } from '@/services/getUserAvatarUrlsV2';
import type { components } from '@primum/api-types';

export type WireComment = components['schemas']['Comment'];

type WireReply = WireComment['replies'][number];

/**
 * Every author on a thread, replies included, whose avatar needs signing. Id 0 is the
 * anonymous persona when the API could not resolve it, and has no avatar to ask for.
 */
export const authorIdsOf = (comments: WireComment[]) => {
  const userIds = new Set<string>();

  const add = (userId: number) => {
    if (userId) {
      userIds.add(userId.toString());
    }
  };

  comments.forEach((comment) => {
    add(comment.userId);

    comment.replies?.forEach((reply) => add(reply.userId));
  });

  return Array.from(userIds);
};

/** The author with their avatar replaced by its signed URL, when one was signed. */
const withSignedAvatar = (
  user: WireComment['user'],
  signedUrls: UserAvatarUrlLookup,
  userId: number
): CommentAuthor => {
  const signedAvatar = signedUrls.get(userId);

  return {
    ...user,
    avatar: signedAvatar
      ? {
          Location128px: signedAvatar.Location128px || undefined,
          Bucket: signedAvatar.Bucket || undefined,
          ETag: signedAvatar.ETag || undefined,
          Key: signedAvatar.Key || undefined,
          Location: signedAvatar.Location || undefined,
          ServerSideEncryption: undefined,
          isPublic: signedAvatar.isPublic || undefined,
        }
      : user.avatar,
  } satisfies CommentAuthor;
};

const toReply = (reply: WireReply, signedUrls: UserAvatarUrlLookup): CommentReply =>
  ({
    ...reply,
    user: withSignedAvatar(reply.user, signedUrls, reply.userId),
  }) satisfies CommentReply;

/**
 * The anti-corruption layer: every wire comment becomes a domain comment here
 * and nowhere else, with each author's avatar signed. A comment with no
 * replies field gets an empty list, which is what the thread draws.
 */
export const toComment = (comment: WireComment, signedUrls: UserAvatarUrlLookup): Comment =>
  ({
    ...comment,
    user: withSignedAvatar(comment.user, signedUrls, comment.userId),
    replies: comment.replies?.map((reply) => toReply(reply, signedUrls)) || [],
  }) satisfies Comment;
